// apps/web/src/layouts/components/WebPushToggle.tsx
//
// Toggle "Ativar notificações neste dispositivo" no rodapé do sininho.
// Some por completo quando o transporte não está disponível (navegador sem
// suporte OU VAPID off no backend) — NO-OP sem crash.
// Contrato: docs/contracts/ESCR-web-push-transport.md.

import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Switch } from '@alamo/ui';

import { useWebPushDevice } from './useWebPushDevice';

export function WebPushToggle() {
  const { t } = useTranslation(['common']);
  const { status, busy, available, enable, disable, lastError } = useWebPushDevice();

  // Transporte indisponível → não renderiza nada (feature escondida).
  if (!available) return null;

  const isOn = status === 'subscribed';
  const isDenied = status === 'denied' || lastError === 'denied';

  async function handleToggle(next: boolean): Promise<void> {
    if (next) {
      await enable();
      if (lastError === 'denied') {
        toast.error(t('common:notifications.push.denied'), { duration: 6000 });
        return;
      }
      if (lastError === 'error') {
        toast.error(t('common:notifications.push.error'));
        return;
      }
      toast.success(t('common:notifications.push.enabled'));
    } else {
      await disable();
      if (lastError === 'error') {
        toast.error(t('common:notifications.push.error'));
        return;
      }
      toast.success(t('common:notifications.push.disabled'));
    }
  }

  return (
    <div
      className="flex items-center justify-between gap-3 border-t border-slate-200 p-4"
      data-testid="web-push-toggle"
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-secondary-950">
          {t('common:notifications.push.toggle_label')}
        </p>
        <p className="text-xs text-slate-500">
          {isDenied
            ? t('common:notifications.push.denied')
            : t('common:notifications.push.toggle_hint')}
        </p>
      </div>
      <Switch
        checked={isOn}
        disabled={busy || isDenied}
        onChange={(next) => {
          void handleToggle(next);
        }}
        aria-label={t('common:notifications.push.toggle_label')}
      />
    </div>
  );
}
